<script setup lang="ts">
import { reactive, ref, toRaw, watch } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import EmailOrUsernameField from '@/components/fields/EmailOrUsernameField.vue';
import PasswordField from '@/components/fields/PasswordField.vue';
import { requiredValidationRule } from '@/helpers/validationRules';
import { brandConfig } from '@/brand';

export interface InitialData {
  username?: string;
  password?: string;
}

export interface ValidData {
  username: string;
  password: string;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

const formData = reactive<InitialData>({});
const form = ref<HTMLFormElement | null>(null);
const isValid = ref(true);

watch(
  () => props.initialData,
  (initialData) => {
    formData.username = initialData.username;
    formData.password = initialData.password;
  },
  { immediate: true },
);

async function handleSubmit(): Promise<void> {
  if (form.value === null) return;
  const { valid } = await form.value.validate();
  if (valid === false) return;
  emit('submit', toRaw(formData) as ValidData);
}
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    class="mt-4 rounded border bg-white px-8 py-6"
    :class="$style.formCard"
    data-testid="loginForm"
    @submit.prevent="handleSubmit"
  >
    <h2 class="text-2xl font-semibold">{{ brandConfig.loginWelcome }}</h2>
    <EmailOrUsernameField
      v-model="formData.username"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
    />
    <PasswordField
      v-model="formData.password"
      class="mt-2"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
    />
    <div class="flex justify-end">
      <KBtn
        type="submit"
        size="lg"
        append-icon="mdi-login-variant"
        :loading="loading"
        :disabled="form?.isValid === false"
        class="mt-4"
      >
        Login
      </KBtn>
    </div>
  </v-form>
</template>

<style module lang="scss">
@use '@/styles/colors';

.formCard {
  border-color: colors.$color-blue-400;
}
</style>
